import React from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { Form, Modal } from "antd";
import { createStructuredSelector } from "reselect";

import {
  selectFormPopups,
  selectSelectedView,
  selectFilters,
  selectIsFetching,
  selectFilterList,
  getUserSelectedColumns,
  selectAssigneesAndUserGroups,
  selectAllUsersAndUserGroups,
  selectUserGroupsWithUsers,
  selectUser
} from "../../store/selector";
import { formPopupsContentKeys } from "../../constant/formPopupsContentKeys";
import ACTIONS from "../../store/action";
import { ApiNames } from "../../api/api";
import { selectedViews } from "../../constant/table";
import { getPageKeyForSelectedView } from "../../utils/pendo/getPageKeyForSelectedView";
import { ReactComponent as WarningIcon } from "../../assets/svg/warning-icon-popup.svg";
import "./FormPopupsModal.css";
import SavedListComponent from "@tailormed/common-client/component/SavedList/SavedListComponent.js";
import { LIST_TYPES } from "@tailormed/common-client/constant/savedList";
import { parseViewers } from "@tailormed/common-client/util/savedList";
import { deepEqual } from "../../utils/misc";
import { getColumnLayoutStorageKey, readColumnLayoutFromSession } from "../../utils/columnLayout";
import {
  useStandardizedTracking,
  EventNames,
  FeatureGroups,
  EEContentKeys,
  LayoutTypes
} from "../../utils/pendo/useStandardizedTracking";

const AddFilterListFormComponent = ({
  t,
  setFormPopupsAct,
  formPopups,
  selectedView,
  filters,
  createFilterListAct,
  isFetching,
  filterList,
  updateFilterListAct,
  userSelectedColumns
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { visible, contentKey, isDuplicateMode } = formPopups;
  const [modal, contextHolder] = Modal.useModal();
  const assigneesAndGroups = useSelector(selectAssigneesAndUserGroups) || []; // For dropdown (assignable only)
  const allUsersAndGroups = useSelector(selectAllUsersAndUserGroups) || []; // For displaying existing viewers
  const userGroupsWithUsers = useSelector(selectUserGroupsWithUsers);
  const currentUser = useSelector(selectUser);
  const setFilterListAct = (filterList) => dispatch(ACTIONS.setFilterList(filterList));
  const { track } = useStandardizedTracking();

  const handleAddButton = (formData) => {
    let finalViewers = formData.viewers?.map((viewer) => viewer.value) || [];
    let existingParams = null;

    if ((filterList.isEditView || isDuplicateMode) && filterList.params) {
      existingParams = typeof filterList.params === "string" ? JSON.parse(filterList.params) : filterList.params;
    }

    const payloadSelectedView = selectedView === selectedViews.savedList ? filterList.selectedView : selectedView;
    const storedLayout = existingParams
      ? { columnWidths: existingParams.columnWidths, columnOrder: existingParams.columnOrder }
      : {};
    // On the saved-list management page we are not viewing the list, so its scoped-session
    // layout may be stale/unsaved from an earlier visit — only trust the live session layout
    // when actually viewing a list (or customizing the main dashboard).
    const isViewingTable = selectedView !== selectedViews.savedList;

    let columnLayout;
    if ((filterList.isEditView || !isViewingTable) && existingParams) {
      // Editing metadata, or duplicating from the management page: keep the source list's
      // persisted layout untouched.
      columnLayout = storedLayout;
    } else {
      // Fresh create or "save as new" from a viewed list: capture the layout currently on
      // screen via the list-scoped key (viewed list) or the plain view key (main dashboard).
      const liveLayout = readColumnLayoutFromSession(getColumnLayoutStorageKey(payloadSelectedView, filterList?.id));
      const hasLiveLayout = liveLayout.columnOrder?.length > 0 || Object.keys(liveLayout.columnWidths).length > 0;
      columnLayout = hasLiveLayout ? liveLayout : storedLayout;
    }

    if (isDuplicateMode && formData.type === LIST_TYPES.SHARED && currentUser?.id !== filterList?.user) {
      const originalOwnerId = filterList.user;
      const isOwnerAlreadyInViewers = finalViewers.some((viewerId) => viewerId === originalOwnerId);

      if (!isOwnerAlreadyInViewers) {
        const originalOwner = allUsersAndGroups.find((item) => item.id === originalOwnerId);

        if (originalOwner) {
          finalViewers = [...finalViewers, originalOwner.id];
        }
      }
    }

    const filterListValues = {
      name: formData.name.trim(),
      description: formData.description?.trim(),
      type: formData.type || LIST_TYPES.PRIVATE,
      params:
        filterList.isEditView && existingParams
          ? // Editing metadata: keep the list's saved column selection — the live selector reads
            // the current view ("saved-list", all-mandatory columns) and would wipe it (MVP-31327).
            { filters: existingParams.filters, selectedColumns: existingParams.selectedColumns, ...columnLayout }
          : { filters, selectedColumns: userSelectedColumns, ...columnLayout },
      // Note: in case the save action conducted from the saved list view, need to pass the current saved list view value (patient, visits, etc)
      selectedView: payloadSelectedView,
      viewers: finalViewers
    };

    const filtersToSave = filterListValues.params?.filters || filters;
    const hasEEFilter = filtersToSave?.expressEnrollEligible || false;

    track(EventNames.FORM_SUBMITTED, {
      pageKey: getPageKeyForSelectedView(selectedView),
      featureGroup: FeatureGroups.SAVED_LISTS,
      contentKey: filterList.isEditView ? EEContentKeys.SAVED_LIST_UPDATED : EEContentKeys.SAVED_LIST_CREATED,
      layout: LayoutTypes.MODAL,
      additionalInfo: {
        ee_filter_active: hasEEFilter,
        list_type: formData.type || LIST_TYPES.PRIVATE,
        is_duplicate: isDuplicateMode,
        source_view: selectedView
      }
    });

    filterList.isEditView
      ? updateFilterListAct(filterListValues, filterList?.id)
      : createFilterListAct(filterListValues);
  };

  const handleCancelButton = (formData) => {
    if (!formData) {
      cleanForm();
      setFormPopupsAct(false, formPopupsContentKeys.ADD_NEW_LIST);
      return;
    }

    const filterListObject = {
      description: filterList.description || "",
      name: filterList.name || "",
      type: filterList.type || LIST_TYPES.PRIVATE,
      viewers: parseViewers(filterList.viewers)
    };
    const currentFormData = {
      description: formData?.description || "",
      name: formData?.name || "",
      type: formData?.type || LIST_TYPES.PRIVATE,
      viewers: formData?.viewers?.map((viewer) => viewer.value) || []
    };

    // Check for changes in form data OR if there are unsaved changes (including pending visibility change)
    const hasChanges = !deepEqual(currentFormData, filterListObject) || formData?.hasUnsavedChanges;

    hasChanges
      ? modal.confirm({
          className: "formPopupsModal",
          centered: true,
          okText: t("yes_sure"),
          cancelText: t("back"),
          icon: <WarningIcon />,
          title: t("new_list_form.unsaved_details"),
          content: (
            <>
              {t("new_list_form.not_saved_yet")}
              {t("are_you_sure_you_want_to_proceed")}
            </>
          ),
          onOk() {
            cleanForm();
            setFormPopupsAct(false, formPopupsContentKeys.ADD_NEW_LIST);
          }
        })
      : (cleanForm(), setFormPopupsAct(false, formPopupsContentKeys.ADD_NEW_LIST));
  };

  const cleanForm = () => {
    form.resetFields();
    setFilterListAct({ ...filterList, isEditView: false });
  };

  return (
    <>
      <SavedListComponent
        visible={visible && contentKey === formPopupsContentKeys.ADD_NEW_LIST}
        initialData={filterList}
        isEditMode={filterList.isEditView}
        isDuplicateMode={isDuplicateMode}
        handleCancel={handleCancelButton}
        handleSave={handleAddButton}
        isLoading={isFetching}
        assigneesAndGroups={assigneesAndGroups}
        allUsersAndGroups={allUsersAndGroups}
        userGroupsWithUsers={userGroupsWithUsers}
        owner={filterList?.user || null}
        currentUser={currentUser}
        source="financial-navigation"
      />
      {contextHolder}
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  formPopups: selectFormPopups,
  selectedView: selectSelectedView,
  filters: selectFilters,
  filterList: selectFilterList,
  isFetching: selectIsFetching([ApiNames.CreateFilterList, ApiNames.UpdateFilterList]),
  userSelectedColumns: getUserSelectedColumns
});

const mapDispatchToProps = (dispatch) => ({
  setFormPopupsAct: (visible, contentKey, isDuplicateMode = false) =>
    dispatch(ACTIONS.setFormPopups(visible, contentKey, isDuplicateMode)),
  updateFilterListAct: (newFilterListData, filterListId) =>
    dispatch(ACTIONS.updateFilterListAction(newFilterListData, filterListId)),
  createFilterListAct: (filterListData) => dispatch(ACTIONS.createFilterListAction(filterListData))
});

const FilterListForm = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(AddFilterListFormComponent));

export { FilterListForm };
